import './StaffSettingsSection.css'

import { useEffect, useMemo } from 'react'
import { useAtomValue, useSetAtom } from 'jotai'

import { staff } from '../../../store'

import { Button } from '../../../components/Button'
import { DataTable } from '../../../components/DataTable'
import { EmptyState } from '../../../components/EmptyState'
import { PageHeader } from '../../../components/PageHeader'
import { Tag } from '../../../components/Tag'

export function StaffSettingsRegionsView() {
  const session = useAtomValue(staff.staffSessionAtom)
  const regions = useAtomValue(staff.staffRegionsAtom)
  const threshold = useAtomValue(staff.staffRegionsThresholdAtom)
  const isLoading = useAtomValue(staff.staffRegionsIsLoadingAtom)
  const error = useAtomValue(staff.staffRegionsErrorAtom)
  const actionLoading = useAtomValue(staff.staffActionIsLoadingAtom)

  const fetchRegions = useSetAtom(staff.fetchStaffRegionsAtom)
  const patchRegion = useSetAtom(staff.patchStaffRegionAtom)

  const canRead = staff.hasStaffPermission(session, staff.Permission.SettingsRegionsRead)
  const canWrite = staff.hasStaffPermission(session, staff.Permission.SettingsRegionsWrite)

  useEffect(() => {
    if (canRead) fetchRegions()
  }, [canRead, fetchRegions])

  const columns = useMemo(
    () => [
      {
        id: 'location',
        header: 'Region',
        render: (region: staff.StaffRegion) => (
          <div className="staff-settings-section__primary">
            <strong>{region.location || 'Unknown'}</strong>
            <span>
              {region.country} · {region.radiusKm} km
            </span>
          </div>
        ),
      },
      {
        id: 'flags',
        header: 'Status',
        render: (region: staff.StaffRegion) => (
          <div className="staff-settings-section__tags">
            <Tag color={region.isActive ? 'teal' : 'amber'}>
              {region.isActive ? 'active' : 'inactive'}
            </Tag>
            <Tag color={region.isEnabled ? 'violet' : 'red'}>
              {region.isEnabled ? 'enabled' : 'disabled'}
            </Tag>
            {region.forceActivate && <Tag color="gold">force</Tag>}
          </div>
        ),
      },
      {
        id: 'waitlist',
        header: 'Waitlist',
        render: (region: staff.StaffRegion) => region.waitlistCount,
      },
      {
        id: 'actions',
        header: '',
        render: (region: staff.StaffRegion) =>
          canWrite ? (
            <div className="staff-settings-section__actions">
              <Button
                kind="secondary"
                size="small"
                text={region.isEnabled ? 'Disable' : 'Enable'}
                disabled={actionLoading}
                onClick={() => patchRegion({ regionId: region.id, isEnabled: !region.isEnabled })}
              />
              {region.isActive ? (
                <Button
                  kind="ghost"
                  size="small"
                  text="Deactivate"
                  disabled={actionLoading}
                  onClick={() => patchRegion({ regionId: region.id, activate: false })}
                />
              ) : (
                <Button
                  kind="primary"
                  size="small"
                  text="Activate"
                  disabled={actionLoading}
                  onClick={() => patchRegion({ regionId: region.id, activate: true })}
                />
              )}
              <Button
                kind="ghost"
                size="small"
                text={region.forceActivate ? 'Clear force' : 'Force'}
                disabled={actionLoading}
                onClick={() =>
                  patchRegion({
                    regionId: region.id,
                    forceActivate: !region.forceActivate,
                  })
                }
              />
            </div>
          ) : null,
      },
    ],
    [canWrite, actionLoading, patchRegion],
  )

  if (!canRead) {
    return (
      <EmptyState
        title="No regions access"
        description="Your role does not include settings.regions.read."
      />
    )
  }

  return (
    <div className="staff-settings-section">
      <PageHeader
        title="Regions"
        subtitle={`Activation threshold: ${threshold} combined users + waitlist interest.`}
        actions={
          <Button
            kind="secondary"
            text={isLoading ? 'Loading…' : 'Refresh'}
            onClick={() => fetchRegions()}
            disabled={isLoading}
          />
        }
      />

      {error && (
        <EmptyState
          title="Failed to load regions"
          description={error === 'forbidden' ? 'Forbidden.' : 'Request failed.'}
        />
      )}

      {!error && (
        <DataTable
          columns={columns}
          rows={regions}
          getRowKey={(region) => region.id}
          emptyMessage="No regions configured"
        />
      )}
    </div>
  )
}
